import React, { useState, useEffect, ReactNode } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  Tooltip,
  TableFooter,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  AccountBalance,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
  ShoppingCart,
  FilterList,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomerPayment_giftcard_crypto,
  createCustomerPayment_giftcard_crypto_all,
  getCustomersOrders_crypto,
  getCustomersOrders_giftcard_crypto_all,
} from "../../../api/customers/customers";
import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, useIonViewWillEnter } from "@ionic/react";
import ReceiptModal_giftcard_crypto from "../../../components/sales/ReceiptModal_giftcard_crypto";
import { MenuButton } from "../../../components/Menu";

interface OrderItem {
  product_id: string;
  quantity: string;
  product_name: string;
  price: string;
  total_amount: string;
  item_status: string;
   card_amount?: string | null;
  load_type?: string | null;
  card_rate?: string | null;
  naira_rate?: string | null;
  internal_rate?: string | null;
  
  username?: string | null;
  processor_type?: string | null;
  external_processor?: string | null;
  
  customer_bank_code?: string | null;
  customer_account_number?: string | null;
  customer_account_name?: string | null;
  
  otp_status: string | null;
  last_updated: string; // datetime
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  
  product_id_type: string;
  product_id_single: string;
  rmbValue: string;
  commission: string;
  proof_of_payment: string;
  costPrice: string;
  sellingPrice: string;
}

interface Transaction {
  product_name: string;
  order_status: string;
  transactionStartTime: ReactNode;
  id: number; // primary key
  business_id: string;
  branch_id: string | null;
  payout_status: 1 | 0; // 1 = paid out, 0 = not yet
  order_id: string;
  order_code: string;
  order_date: string; // datetime
  active_status: "COMPLETED" | "PENDING" | "CANCELLED" | "REVERSED"; // from enum
  payment_status: 0 | 1 | 2; // 0 = unpaid, 1 = paid, 2 = partial
  
  total_price: string;
  payable_amount?: string | null;
  amount_paid?: string | null;
  balance_due?: number;
  
  mode_of_payment: string | null;
  order_by: string;
  approved_by?: string | null;
  
  member_id: string;
  member_name: string; // added from join
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  
  request_id?: string | null;
  card_amount?: string | null;
  load_type?: string | null;
  card_rate?: string | null;
  naira_rate?: string | null;
  internal_rate?: string | null;
  
  username?: string | null;
  processor_type?: string | null;
  external_processor?: string | null;
  processor_category?: string | null; // Added this field
  
  customer_bank_code?: string | null;
  customer_account_number?: string | null;
  customer_account_name?: string | null;
  
  otp_status: string | null;
  last_updated: string; // datetime
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  
  product_id_type: string;
  product_id_single: string;
  rmbValue: string;
  commission: string;
  proof_of_payment: string;
  costPrice: string;
  sellingPrice: string;
  cryptoType?: string | null;
  cryptoAmount?: string | null;
  product_face_code?: string | null;
staff_name?: string | null;
  
  items: OrderItem[];
}

interface Customer {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
}

const CustomerAccountPage_crypto: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState({
    transactions: false,
    payment: false,
  });
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [totalCardAmount, setTotalCardAmount] = useState(0);
  const [totalCryptoAmount, setTotalCryptoAmount] = useState(0);

  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>(
    []
  );
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [nairaRate, setNairaRate] = useState('');

  // Filter states
  const [externalProcessorFilter, setExternalProcessorFilter] = useState<string>('all');
  const [availableProcessors, setAvailableProcessors] = useState<string[]>([]);

  const [selectedTotals, setSelectedTotals] = useState({
    costPrice: 0,
    sellingPrice: 0,
    rmbValue: 0,
    cardAmount: 0,
    costRate: 0,
    commission: 0
  });

  // Check processor categories for selected transactions
  const [selectedHasChinese, setSelectedHasChinese] = useState(false);
  const [selectedHasUSD, setSelectedHasUSD] = useState(false);
  
  // Check if any transaction has CHINESE processor category
  const hasChineseTransactions = filteredTransactions.some(
    txn => txn.processor_category === 'CHINESE'
  );
  
  // Extract unique external processors from transactions
  useEffect(() => {
    const processors = Array.from(
      new Set(
        transactions
          .map(txn => txn.external_processor)
          .filter(processor => processor && processor.trim() !== '')
      )
    ) as string[];
    
    setAvailableProcessors(processors);
  }, [transactions]);

  // Filter transactions based on external_processor selection
  useEffect(() => {
    if (externalProcessorFilter === 'all') {
      setFilteredTransactions(transactions);
    } else {
      const filtered = transactions.filter(
        txn => txn.external_processor === externalProcessorFilter
      );
      setFilteredTransactions(filtered);
    }
  }, [transactions, externalProcessorFilter]);

  useEffect(() => {
    const cardAmount = filteredTransactions.reduce(
      (sum, txn) => sum + parseFloat(txn.card_amount || "0"),
      0
    );
    setTotalCardAmount(cardAmount);

    const cryptoAmount = filteredTransactions.reduce(
      (sum, txn) => sum + parseFloat(txn.cryptoAmount || "0"),
      0
    );
    setTotalCryptoAmount(cryptoAmount);
  }, [filteredTransactions]);
  
  // Update payment amount based on processor category
  useEffect(() => {
    const selectedTxns = filteredTransactions.filter(txn => 
      selectedTransactions.includes(txn.order_id)
    );
    
    const hasChinese = selectedTxns.some(txn => txn.processor_category === 'CHINESE');
    const hasUSD = selectedTxns.some(txn => txn.processor_category === 'USD');
    
    setSelectedHasChinese(hasChinese);
    setSelectedHasUSD(hasUSD);
    
    let newPaymentAmount = 0;
    
     newPaymentAmount = parseFloat(nairaRate || '0') * selectedTotals.cardAmount;
     
    // if (hasChinese) {
    //   // For CHINESE: payment amount = naira rate * rmb value
    //   newPaymentAmount = parseFloat(nairaRate || '0') * selectedTotals.cardAmount;
    // } else if (hasUSD) {
    //   // For USD: payment amount = total cost price
    //   newPaymentAmount = selectedTotals.costPrice;
    // }
    
    setPaymentAmount(newPaymentAmount);
  }, [nairaRate, selectedTotals.rmbValue, selectedTotals.costPrice, selectedTransactions, filteredTransactions]);

  const unpaidTransactions = filteredTransactions.filter(
    (txn) => txn.payout_status === 0  
  );

  const selectedTransactionTotal = unpaidTransactions
    .filter((txn) => selectedTransactions.includes(txn.order_id))
    .reduce((sum, txn) => {
      const amount = txn.total_price || txn.payable_amount || "0";
      const balanceDue = txn.balance_due || amount;
      const balanceSettled = txn.costPrice || "0";

      return sum + Number(balanceSettled);
    }, 0); 

  const totalOutstanding = unpaidTransactions.reduce((sum, txn) => {
    const amount = parseFloat(txn.total_price);
    const balanceDue = txn.balance_due || amount;
    return sum + balanceDue;
  }, 0);

  const getPaymentStatusLabel = (status: string | number) => {
    const code = Number(status);
    switch (code) {
      case 0:
        return "Unpaid";
      case 1:
        return "Paid";
      case 2:
        return "Partial";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusColor = (status: string | number) => {
    switch (Number(status)) {
      case 0:
        return "error";
      case 1:
        return "success";
      case 2:
        return "warning";
      default:
        return "default";
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const toggleRowExpansion = (orderId: string) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const fetchCustomerTransactions_all = async () => {
    try {
      setLoading((prev) => ({ ...prev, transactions: true }));
      const response = await getCustomersOrders_crypto();

      const processedTransactions = response.data.orders.map(
        (txn: Transaction) => {
          const totalAmount = parseFloat(txn.payable_amount || txn.total_price);
          let balanceDue = Number(txn.balance_due);

          if (txn.payment_status === 2) {
            balanceDue = totalAmount * 0.5; // Placeholder logic
          } else if (txn.payment_status === 1) {
            balanceDue = 0;
          }

          return {
            ...txn,
            balance_due: balanceDue,
          };
        }
      );
      setTransactions(processedTransactions);
      setFilteredTransactions(processedTransactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading((prev) => ({ ...prev, transactions: false }));
    }
  };
  
  const updateSelectedTotals = (selectedIds: string[]) => {
    const selectedTxns = filteredTransactions.filter((txn) =>
      selectedIds.includes(txn.order_id)
    );

    const totals = selectedTxns.reduce(
      (acc, txn) => {
        acc.costPrice += parseFloat(txn.costPrice || '0');
        acc.sellingPrice += parseFloat(txn.sellingPrice || '0');
        acc.rmbValue += parseFloat(txn.rmbValue || '0');
        acc.cardAmount += parseFloat(txn.card_amount || '0');
        acc.commission += parseFloat(txn.commission || '0');
        acc.costRate += parseFloat(txn.costRate || '0');
        return acc;
      },
      { costPrice: 0, sellingPrice: 0, rmbValue: 0, cardAmount: 0, commission: 0 , costRate: 0}
    );

    setSelectedTotals(totals);
  };

  const handleTransactionSelect = (transactionId: string) => {
    const newSelected = selectedTransactions.includes(transactionId)
      ? selectedTransactions.filter((id) => id !== transactionId)
      : [...selectedTransactions, transactionId];

    setSelectedTransactions(newSelected);
    updateSelectedTotals(newSelected);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTransactions([]);
      updateSelectedTotals([]);
    } else {
      const allUnpaidIds = unpaidTransactions.map((txn) => txn.order_id);
      setSelectedTransactions(allUnpaidIds);
      updateSelectedTotals(allUnpaidIds);
    }
    setSelectAll(!selectAll);
  };

  const getPaymentType = () => {
    if (paymentAmount === selectedTransactionTotal) return "exact";
    if (paymentAmount > selectedTransactionTotal) return "overpayment";
    if (paymentAmount < selectedTransactionTotal) return "partial";
    return "exact";
  };
///lola
  const handlePaymentSubmit = async () => {
    if (selectedTransactions.length === 0 || paymentAmount <= 0) return;

    try {
      setLoading((prev) => ({ ...prev, payment: true }));
      const paymentType = getPaymentType();
      
      const selectedTxns = filteredTransactions.filter(txn => selectedTransactions.includes(txn.order_id));
      const customer_id = selectedTxns.length > 0 ? selectedTxns[0].member_id : '';

      const data = {
        customer_id: customer_id,
        order_ids: selectedTransactions,
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_type: paymentType,
        notes: paymentNotes || `Payment for ${selectedTransactions.length} order(s)`,
        naira_rate: nairaRate,
      };

      const response  = await createCustomerPayment_giftcard_crypto_all(data);

      console.log("Payment created successfully:", response);
    // return;
      alert("PayOut Processed successfully.");
      // handleGenerateReceipt(data);
      await fetchCustomerTransactions_all();

      setShowPaymentDialog(false);
      setSelectedTransactions([]);
      setPaymentAmount(0);
      setPaymentNotes("");
      setSelectAll(false);
      setNairaRate('');
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setLoading((prev) => ({ ...prev, payment: false }));
    }
  };
  
  useEffect(() => {
    setSelectAll(
      selectedTransactions.length === unpaidTransactions.length &&
        unpaidTransactions.length > 0
    );
  }, [selectedTransactions, unpaidTransactions]);

  useIonViewWillEnter(() => {
    fetchCustomerTransactions_all();
  });

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);

  const handleGenerateReceipt = (paymentData: any) => {
    const paidTransactions = filteredTransactions.filter(txn => 
      paymentData.order_ids.includes(txn.order_id)
    );

    if (paidTransactions.length === 0) return;

    const totalAmount = paidTransactions.reduce((sum, txn) => {
      return sum + txn.items.reduce((txnSum, item) => {
        return txnSum + (parseFloat(item.total_amount) || 0);
      }, 0);
    }, 0);

    const initialBalanceDue = paidTransactions.reduce((sum, txn) => {
      return sum + (txn.balance_due || 0);
    }, 0);

    const paidAmount = paymentData.amount;
    const actualBalanceDue = Math.max(0, initialBalanceDue - paidAmount);

    const receiptItems = paidTransactions.flatMap(txn => 
        txn.items.map(item => ({
            orderID: txn.order_id,
            productName: item.product_name || 'Unknown Product',
            quantity: item.quantity || '0',
            unitPrice: parseFloat(item.price) || 0,
            amount: parseFloat(item.total_amount) || 0,
            status: item.item_status || 'COMPLETED',
            product_id_type: txn.product_id_type || 'N/Ap',
            cryptoType: txn.cryptoType || 'N/Ap',
            cryptoAmount: txn.cryptoAmount || 'N/Ap',
        }))
    );
    
    const firstTransaction = paidTransactions[0];

    setReceiptData({
      customer: {
        name: `${firstTransaction.member_name}`,
        phone: firstTransaction.customer_phone || 'N/A',
        email: firstTransaction.customer_email || 'N/A'
      },
      order: {
        id: paidTransactions.length === 1 
          ? firstTransaction.order_code 
          : `Multiple Orders (${paidTransactions.length})`,
        date: new Date().toISOString(),
        status: 'COMPLETED',
        paymentStatus: '1',
        paymentMethod: paymentData.payment_method || 'cash'
      },
      items: receiptItems,
      summary: {
        totalAmount: totalAmount,
        paidAmount,
        balanceDue: actualBalanceDue
      },
      payment: {
        amount: paidAmount,
        method: paymentData.payment_method || 'cash',
        date: new Date().toISOString(),
        notes: paymentData.notes || `Payment for ${paidTransactions.length} order(s)`
      }
    });
    setReceiptOpen(true);
  };

  const handlePrintReceipt = (transaction: Transaction) => {
    const receiptItems = [{
      orderID: transaction.order_id,
      productName: transaction.product_name || 'Unknown Product',
      quantity: transaction.card_amount || '0',
      unitPrice: parseFloat(transaction.sellingPrice) || 0,
      amount: parseFloat(transaction.sellingPrice) || 0,
      status: transaction.order_status || 'COMPLETED',
      product_id_type: transaction.product_id_type || 'N/Ak',
      cryptoType: transaction.cryptoType || 'N/Ak',
      cryptoAmount: transaction.cryptoAmount || 'N/Ak',
     }];

    const totalAmount = parseFloat(transaction.payable_amount || transaction.total_price);
    const paidAmount = totalAmount - (transaction.balance_due || 0);

    setReceiptData({
      customer: {
        name: transaction.member_name || transaction.customer_name || "N/A",
        phone: transaction.customer_phone || "N/A",
        email: transaction.customer_email || "N/A",
      },
      order: {
        id: transaction.order_code,
        date: transaction.order_date,
        status: transaction.order_status,
        paymentStatus: getPaymentStatusLabel(transaction.payment_status),
        paymentMethod: transaction.mode_of_payment || 'cash'
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue: transaction.balance_due || 0
      }
    });
    setReceiptOpen(true);
  };

  const totalPayableAmount = filteredTransactions.reduce(
    (sum, txn) =>
      sum + parseFloat(txn.payable_amount || txn.total_price || "0"),
    0
  );

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <MenuButton />  
          <IonTitle> Verify Crypto PayOut</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* <Typography variant="h4" gutterBottom>
            Verify Crypto PayOut
          </Typography> */}

          {/* Filter Section
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FilterList color="action" />
               
              

              <Chip 
                label={`Showing: ${filteredTransactions?.length} transactions`}
                variant="outlined"
                color="primary"
              />
            </Box>
          </Paper> */}

          {/* Balance and Payment Summary */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <Receipt sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Total Value</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {formatCurrencyN(totalPayableAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: "warning.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Warning sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Outstanding</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {formatCurrencyN(totalOutstanding)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
           
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Total Crypto Amount</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {(totalCryptoAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Payment Actions */}
          {unpaidTransactions.length > 0 && (
            <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Payment />}
                onClick={() => setShowPaymentDialog(true)}
                disabled={selectedTransactions.length === 0}
              >
                Process Payout Payment ({selectedTransactions.length} selected)
              </Button>
            </Box>
          )}

          {/* Orders Table */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Transaction History
          </Typography>
          
          {unpaidTransactions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    indeterminate={
                      selectedTransactions.length > 0 && !selectAll
                    }
                  />
                }
                label={`Select All Unpaid Orders`}
              />
            </Box>
          )}

          {loading.transactions ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredTransactions.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Select</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Staff Name</TableCell>

                    <TableCell>Customer</TableCell>

                     <TableCell align="right">Crypto Amount</TableCell>
                    <TableCell align="right">Crypto Type</TableCell>
                    <TableCell align="right">Cost Rate </TableCell>
                    <TableCell align="right">Selling Rate</TableCell>

              
                    
                    <TableCell align="right">Cost Price</TableCell>
                    <TableCell align="right">Selling Price</TableCell>
                    <TableCell align="right">Commission</TableCell>

                    <TableCell align="right">Crypto Value</TableCell>

                                        <TableCell>OM. Notes</TableCell>

                     <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((txn) => {
                    const isUnpaid =
                      txn.payout_status === 0 
                    const isSelected = selectedTransactions.includes(
                      txn.order_id
                    );
                    const isExpanded = expandedRows.includes(txn.order_id);
                    const isChineseProcessor = txn.processor_category === 'CHINESE';

                    return (
                      <React.Fragment key={txn.order_id}>
                        <TableRow
                          sx={{
                            bgcolor: isSelected
                              ? "action.selected"
                              : "inherit",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <TableCell padding="checkbox">
                            { isUnpaid && (
                              <Checkbox
                                checked={isSelected}
                                onChange={() =>
                                  handleTransactionSelect(txn.order_id)
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell>{txn.transactionStartTime}</TableCell>
                          <TableCell>{txn.staff_name}</TableCell>
                          <TableCell>{txn.member_name} </TableCell>
                        
                          <TableCell align="right">{(parseFloat(txn.cryptoAmount || '0'))}</TableCell>
                          <TableCell align="right">{txn.cryptoType}</TableCell>
                          
                          {/* Conditionally show RMB Value and Naira Rate cells only for CHINESE processors */}
                      <TableCell align="right">{formatCurrencyN(parseFloat(txn.costRate || '0'))}</TableCell>

                        <TableCell align="right">{formatCurrencyN(parseFloat(txn.sellingRate || '0'))}</TableCell>
                          
                          <TableCell align="right">{formatCurrencyN(parseFloat(txn.costPrice || '0'))}</TableCell>
                          <TableCell align="right">{formatCurrencyN(parseFloat(txn.sellingPrice || '0'))}</TableCell>
                          <TableCell align="right">{formatCurrencyN(parseFloat(txn.commission || '0'))}</TableCell>
                           <TableCell align="center">

                            {txn.product_face_code ? <>{txn.product_face_code}</> : '-'}


                           </TableCell>
    <TableCell align="right">{ ( (txn.note2 || 'nill'))}</TableCell>

                          <TableCell align="right">
                            <Chip
                              label={getPaymentStatusLabel(txn.payment_status)}
                              size="small"
                              color={
                                getPaymentStatusColor(txn.payment_status) as any
                              }
                              icon={
                                Number(txn.payment_status) === 1 ? (
                                  <CheckCircle fontSize="small" />
                                ) : undefined
                              }
                            />
                            <Tooltip title="Print receipt">
                              <IconButton
                                size="small"
                                onClick={() => handlePrintReceipt(txn)}
                                color="primary"
                              >
                                Print
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* Expandable row for order items */}
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={hasChineseTransactions ? 15 : 13} // Adjust colspan based on conditional columns
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ margin: 1 }}>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  component="div"
                                >
                                  Order Items
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Product</TableCell>
                                      <TableCell align="right">
                                        Quantity
                                      </TableCell>
                                      <TableCell align="right">
                                        Unit Price
                                      </TableCell>
                                      <TableCell align="right">
                                        Total
                                      </TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {txn.items.map((item, index) => (
                                      <TableRow
                                        key={`${txn.order_id}-${index}`}
                                      >
                                        <TableCell>
                                          {item.product_name}
                                        </TableCell>
                                        <TableCell align="right">
                                          {item.quantity}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatCurrencyN(
                                            parseFloat(item.price)
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatCurrencyN(
                                            parseFloat(item.total_amount)
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={item.item_status}
                                            size="small"
                                            color={
                                              item.item_status ===
                                              "COMPLETED"
                                                ? "success"
                                                : "default"
                                            }
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                {externalProcessorFilter === 'all' 
                  ? "No transactions found." 
                  : `No transactions found for processor: ${externalProcessorFilter}`}
              </Typography>
            </Paper>
          )}
        </Box>
      </IonContent>

      {/* Payment Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Process Payout Payment for Selected Orders
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={() => setShowPaymentDialog(false)}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Selected Orders: {selectedTransactions.length}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Total Cost Price: <strong>{formatCurrencyN(selectedTotals.costPrice)}</strong>
              </Typography>
              <Typography variant="body2" gutterBottom>
                Total Selling Price: <strong>{formatCurrencyN(selectedTotals.sellingPrice)}</strong>
              </Typography>
              
              {/* Conditionally show RMB Value only for CHINESE processors */}
              {selectedHasChinese && (
                <Typography variant="body2" gutterBottom>
                  Total RMB Value: <strong>{(selectedTotals.rmbValue)}</strong>
                </Typography>
              )}
              
              <Typography variant="body2" gutterBottom>
                Total Crypto Amount: <strong>{(selectedTotals.cardAmount)}</strong>
              </Typography>

                <Typography variant="body2" gutterBottom>
    Total Cost Rate: <strong>{formatCurrencyN(selectedTotals.costRate)}</strong>
  </Typography>

              <Typography variant="body2" gutterBottom>
                Total Commission: <strong>{formatCurrencyN(selectedTotals.commission)}</strong>
              </Typography>

              <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Orders:
                </Typography>
                {unpaidTransactions
                  .filter((txn) =>
                    selectedTransactions.includes(txn.order_id)
                  )
                  .map((txn) => (
                    <Box
                      key={txn.order_id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {txn.order_code} ({txn.member_name})
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold" }}
                      >
                        {formatCurrencyN(txn.sellingPrice ? parseFloat(txn.sellingPrice) : 0)}
                      </Typography>
                    </Box>
                  ))}
              </Box>

              {paymentAmount > 0 && (
                <Alert
                  severity={
                    getPaymentType() === "exact"
                      ? "success"
                      : getPaymentType() === "overpayment"
                      ? "info"
                      : "warning"
                  }
                  sx={{ mt: 2 }}
                >
                  {getPaymentType() === "exact" &&
                    "Exact payment - all selected orders will be marked as paid"}
                  {getPaymentType() === "overpayment" &&
                    `Excess Profit of ${formatCurrencyN(
                      paymentAmount - selectedTransactionTotal
                    )} `}
                  {getPaymentType() === "partial" &&
                    `Loss of - ${formatCurrencyN(
                      selectedTransactionTotal - paymentAmount
                    )} `}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Conditionally show Naira Rate field only for CHINESE processors */}
              {/* {selectedHasChinese && (
              
              )}
               */}
                 <TextField
                  fullWidth
                  label="Current Naira Rate"
                  type="number"
                  value={nairaRate}
                  onChange={(e) => setNairaRate(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₦</InputAdornment>
                    ),
                  }}
                />
              <TextField
                fullWidth
                label="Payment Amount"
                type="number"
                value={paymentAmount}
                disabled
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₦</InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Payment Notes"
                multiline
                rows={3}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Optional payment notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePaymentSubmit}
            disabled={
              !paymentAmount ||
              selectedTransactions.length === 0 ||
              loading.payment
            }
            startIcon={
              loading.payment ? <CircularProgress size={20} /> : <Receipt />
            }
          >
            Process Payout Payment ({formatCurrencyN(paymentAmount)})
          </Button>
        </DialogActions>
      </Dialog>

      { receiptData && (
        <ReceiptModal_giftcard_crypto
          open={receiptOpen}
          onClose={() => setReceiptOpen(false)}
          receiptData={receiptData}
        />
      )}
    </IonPage>
  );
};

export default CustomerAccountPage_crypto;